import { useEffect, useRef, useState } from 'react';
import { Mail, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { TRIAGEM_BTN, TRIAGEM_CELULA } from '@/components/kanban/triagem/triagemTabela';
import { MiniConversaSheet } from '@/components/qualidade/MiniConversaSheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { ROTULO_RETORNO, chamarCreditoClaro, type CodigoCr, type CotacaoNaClaro, type EstadoClaro, type GerenteClaro } from './useCreditoNaClaro';

const dia = (s: string) => new Date(s).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'America/Sao_Paulo' });
const hora = (s: string) => new Date(s).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });

/* O CRÉDITO NA CLARO (Douglas 22/10/2026, maquete v4 aprovada): tudo dentro da
   triagem do Crédito. Os códigos CR da Claro, o retorno de cada defesa, a
   cobrança por e-mail à Cristiane e ao Marlon (depois do 1º retorno) e o
   WhatsApp dos gerentes na gaveta do Inbox (MiniConversaSheet, a mesma do
   cartão do Kanban). O backend decide quando dá para cobrar. */

/** A coluna "Claro" da linha: onde a cotação está na Claro. */
export function CelulaClaro({ estado }: { estado: EstadoClaro | undefined }) {
  if (!estado) return <span className={TRIAGEM_CELULA}>—</span>;
  const enviados = estado.emails.filter(e => e.status === 'enviada');
  const ultimo = enviados[enviados.length - 1];
  const d = estado.ultima_defesa;
  const titulo = ultimo
    ? (ultimo.resposta_em ? `A Claro respondeu ${dia(ultimo.resposta_em)}` : `${ultimo.numero}º e-mail ${dia(ultimo.enviado_em)}`)
    : !d ? 'Sem defesa'
      : d.retorno ? `Retorno: ${ROTULO_RETORNO[d.retorno] ?? d.retorno}` : `Pipefy ${d.numero}`;
  const sub = ultimo && !ultimo.resposta_em
    ? (estado.pode_cobrar ? 'pode cobrar de novo' : 'aguardando a Claro (48 h)')
    : !ultimo && d && !d.retorno ? (estado.pode_cobrar ? 'sem retorno em 24 h' : 'aguardando o retorno')
      : estado.pode_cobrar ? 'pode cobrar a Claro' : '';
  return <span className="block min-w-0">
    <span className={cn('block truncate text-xs', ultimo?.resposta_em ? 'font-semibold text-[#075E54] dark:text-[#25D366]' : 'text-primary')}>{titulo}</span>
    {sub && <span className={TRIAGEM_CELULA}>{sub}</span>}
  </span>;
}

/** Os dois ícones novos da linha: cobrar na Claro e o WhatsApp do gerente. */
export function BotoesClaro({ estado, gerentes, onCobrar, onWhatsapp }: {
  estado: EstadoClaro | undefined; gerentes: GerenteClaro[]; onCobrar: () => void; onWhatsapp: (g: GerenteClaro) => void;
}) {
  return <>
    <Button size="icon" variant="ghost" className={TRIAGEM_BTN} disabled={!estado?.pode_cobrar} onClick={onCobrar}
      title={estado?.pode_cobrar ? 'Cobrar na Claro: e-mail à Cristiane e ao Marlon' : estado?.bloqueio || 'Cobrar na Claro'}><Mail /></Button>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="icon" variant="ghost" className={TRIAGEM_BTN} title="WhatsApp do gerente da Claro" disabled={!gerentes.some(g => g.telefone)}><MessageCircle /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {gerentes.filter(g => g.telefone).map(g => <DropdownMenuItem key={g.id} onClick={() => onWhatsapp(g)}>WhatsApp {g.nome.split(' ')[0]}</DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>
  </>;
}

/** "Cobranças na Claro", na cotação aberta. */
export function CobrancasNaClaro({ estado, gerentes, onCobrar, onWhatsapp }: {
  estado: EstadoClaro | undefined; gerentes: GerenteClaro[]; onCobrar: () => void; onWhatsapp: (g: GerenteClaro) => void;
}) {
  const emails = estado?.emails ?? [];
  const proximo = estado?.ultimo_email_em && !estado.pode_cobrar ? 'A Claro tem 48 h úteis para responder ao último e-mail.' : null;
  return <article className="rounded-lg border border-border p-3 text-sm">
    <p className="font-semibold">Cobranças na Claro</p>
    {!emails.length && <p className="text-xs text-muted-foreground">Nenhum e-mail à Claro ainda. O botão libera depois do 1º retorno da defesa (manteve a recusa ou 24 h sem resposta).</p>}
    {emails.map(e => <p key={`${e.numero}-${e.enviado_em}`} className="mt-1 text-xs">
      <span className="font-semibold">{e.numero}º e-mail</span> · {hora(e.enviado_em)}{e.por ? ` · por ${e.por}` : ''}
      {e.status !== 'enviada' ? <span className="text-danger"> · {e.status === 'incerta' ? 'envio não confirmado' : 'não saiu'}</span>
        : e.resposta_em ? <span className="font-semibold text-[#075E54] dark:text-[#25D366]"> · a Claro respondeu {hora(e.resposta_em)}</span>
          : <span className="text-muted-foreground"> · sem resposta</span>}
    </p>)}
    {(proximo || (!estado?.pode_cobrar && estado?.bloqueio)) && <p className="mt-1 text-xs text-primary">{proximo || estado?.bloqueio}</p>}
    <div className="mt-2 flex flex-wrap gap-2">
      <Button size="sm" disabled={!estado?.pode_cobrar} onClick={onCobrar}>
        Cobrar na Claro · {emails.filter(e => e.status === 'enviada').length + 1}º e-mail
      </Button>
      {gerentes.filter(g => g.telefone).map(g => <Button key={g.id} size="sm" variant="outline" onClick={() => onWhatsapp(g)}>WhatsApp {g.nome.split(' ')[0]}</Button>)}
    </div>
  </article>;
}

/** Os códigos CR para escolher (defesa nova e retorno). */
export function EscolherCodigos({ codigos, marcados, onChange, disabled }: {
  codigos: CodigoCr[]; marcados: string[]; onChange: (v: string[]) => void; disabled?: boolean;
}) {
  const alternar = (c: string) => onChange(marcados.includes(c) ? marcados.filter(x => x !== c) : [...marcados, c]);
  const escolhidos = codigos.filter(c => marcados.includes(c.codigo) && c.defesa_leva);
  return <div className="space-y-1.5">
    <div className="flex flex-wrap gap-1.5">
      {codigos.map(c => <button key={c.codigo} type="button" disabled={disabled} onClick={() => alternar(c.codigo)} aria-pressed={marcados.includes(c.codigo)}
        className={cn('rounded-md border px-2 py-1 text-left text-[11px]', marcados.includes(c.codigo) ? 'border-primary bg-primary/10 font-semibold text-primary' : 'border-border text-foreground')}>
        {c.codigo.startsWith('CR#') ? <b>{c.codigo} </b> : null}{c.nome}
      </button>)}
    </div>
    {escolhidos.map(c => <p key={c.codigo} className="text-[11px] text-muted-foreground"><b>A defesa leva ({c.codigo.startsWith('CR#') ? c.codigo : c.nome}):</b> {c.defesa_leva}</p>)}
  </div>;
}

export function RetornoDefesaDialog({ alvo, onClose, codigos, salvar, salvando }: {
  alvo: { defesaIds: string[]; numero: string } | null; onClose: () => void; codigos: CodigoCr[];
  salvar: (b: { defesaIds: string[]; retorno: string; codigos: string[]; obs: string }) => Promise<unknown>; salvando: boolean;
}) {
  const [retorno, setRetorno] = useState('');
  const [marcados, setMarcados] = useState<string[]>([]);
  const [obs, setObs] = useState('');
  useEffect(() => { setRetorno(''); setMarcados([]); setObs(''); }, [alvo]);
  if (!alvo) return null;
  const pode = !!retorno && (retorno !== 'manteve' || marcados.length > 0) && !salvando;
  const confirmar = async () => {
    try { await salvar({ defesaIds: alvo.defesaIds, retorno, codigos: marcados, obs: obs.trim() }); toast.success('Retorno da defesa registrado.'); onClose(); }
    catch (e) { toast.error(e instanceof Error ? e.message : 'Não foi possível registrar o retorno.'); }
  };
  return <Dialog open onOpenChange={o => { if (!o && !salvando) onClose(); }}>
    <DialogContent className="sm:max-w-[560px]">
      <DialogHeader><DialogTitle>Retorno da defesa {alvo.numero}</DialogTitle>
        <DialogDescription>O que a Claro respondeu no Pipefy. Manteve a recusa ou 24 h sem resposta libera a cobrança na Claro.</DialogDescription></DialogHeader>
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(ROTULO_RETORNO).map(([v, r]) => <button key={v} type="button" onClick={() => setRetorno(v)} aria-pressed={retorno === v}
          className={cn('rounded-md border px-2.5 py-1 text-xs', retorno === v ? 'border-primary bg-primary/10 font-semibold text-primary' : 'border-border')}>{r}</button>)}
      </div>
      {retorno === 'manteve' && <><Label>Código que a Claro mandou ao manter (obrigatório)</Label><EscolherCodigos codigos={codigos} marcados={marcados} onChange={setMarcados} disabled={salvando} /></>}
      <Label htmlFor="retorno-obs">O que veio no retorno (opcional)</Label>
      <Textarea id="retorno-obs" value={obs} onChange={e => setObs(e.target.value)} rows={3} disabled={salvando} />
      <div className="flex justify-end gap-2"><Button variant="outline" disabled={salvando} onClick={onClose}>Cancelar</Button><Button disabled={!pode} onClick={() => void confirmar()}>Registrar retorno</Button></div>
    </DialogContent>
  </Dialog>;
}

export function CobrarCreditoDialog({ alvo, onClose, onEnviado }: { alvo: CotacaoNaClaro | null; onClose: () => void; onEnviado: () => void }) {
  const [solar, setSolar] = useState('');
  const [texto, setTexto] = useState('');
  const [previa, setPrevia] = useState<{ numero: number; para: string[]; assunto: string } | null>(null);
  const [ocupado, setOcupado] = useState(false);
  useEffect(() => { setSolar(''); setTexto(''); setPrevia(null); }, [alvo]);
  if (!alvo) return null;
  const montar = async () => {
    setOcupado(true);
    try {
      const p = await chamarCreditoClaro<{ numero: number; para: string[]; assunto: string; texto: string }>('previa', 'POST', { pedidoId: alvo.pedidoId, cotacao: alvo.cotacao, solar });
      setPrevia(p); setTexto(p.texto);
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Não foi possível montar o e-mail.'); } finally { setOcupado(false); }
  };
  const enviar = async () => {
    setOcupado(true);
    try {
      const r = await chamarCreditoClaro<{ numero: number }>('enviar', 'POST', { pedidoId: alvo.pedidoId, cotacao: alvo.cotacao, solar, texto });
      toast.success(`${r.numero}º e-mail à Claro enviado. Está nas cobranças da cotação.`);
      onEnviado(); onClose();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Não foi possível enviar.'); } finally { setOcupado(false); }
  };
  return <Dialog open onOpenChange={o => { if (!o && !ocupado) onClose(); }}>
    <DialogContent className="sm:max-w-[640px]">
      <DialogHeader><DialogTitle>Cobrar na Claro · pedido {alvo.numeroPedido}</DialogTitle>
        <DialogDescription>{alvo.cliente} · cotação {alvo.cotacao || 'sem número'}. Sai pela atendimento@, com a sua assinatura, e fica nas cobranças desta cotação.</DialogDescription></DialogHeader>
      <Label htmlFor="credito-solar">O que o SOLAR mostra hoje (obrigatório, entra no e-mail)</Label>
      <Textarea id="credito-solar" value={solar} onChange={e => { setSolar(e.target.value); setPrevia(null); }} rows={2} disabled={ocupado}
        placeholder="Ex.: Pipefy aprovado, SOLAR segue reprovado por Rating PDV Sócio" />
      {!previa ? <div className="flex justify-end gap-2"><Button variant="outline" disabled={ocupado} onClick={onClose}>Cancelar</Button>
        <Button disabled={ocupado || solar.trim().length < 10} onClick={() => void montar()}>Montar o e-mail</Button></div>
        : <>
          <p className="text-xs text-muted-foreground"><b>Para:</b> {previa.para.join(', ')}<br /><b>Assunto:</b> {previa.assunto}</p>
          <Textarea value={texto} onChange={e => setTexto(e.target.value)} rows={12} disabled={ocupado} className="text-xs" />
          <div className="flex justify-end gap-2"><Button variant="outline" disabled={ocupado} onClick={onClose}>Cancelar</Button>
            <Button disabled={ocupado || texto.trim().length < 40} onClick={() => void enviar()}>Enviar o {previa.numero}º e-mail</Button></div>
        </>}
    </DialogContent>
  </Dialog>;
}

/** A gaveta do Inbox com a conversa do gerente, a mensagem do pedido já escrita. */
export function GavetaGerente({ alvo, onClose }: { alvo: { gerente: GerenteClaro; cotacao: CotacaoNaClaro } | null; onClose: () => void }) {
  const [conversa, setConversa] = useState<{ conversation_id: string; telefone: string } | null>(null);
  const fechar = useRef(onClose);
  fechar.current = onClose;
  useEffect(() => {
    if (!alvo) { setConversa(null); return; }
    let vivo = true;
    chamarCreditoClaro<{ conversation_id: string; telefone: string }>(`gerentes/${alvo.gerente.id}/conversa`, 'POST', {})
      .then(c => {
        if (!vivo) return;
        const c2 = alvo.cotacao;
        const texto = `${alvo.gerente.nome.split(' ')[0]}, o pedido ${c2.numeroPedido} (${c2.cliente}${c2.cotacao ? `, cotação ${c2.cotacao}` : ''}) segue reprovado no crédito`
          + `${c2.codigos.length ? ` (${c2.codigos.join(', ')})` : ''}.${c2.pipefy ? ` A defesa está no Pipefy ${c2.pipefy}, com o print do crédito.` : ''} Consegue ver para nós?`;
        try { localStorage.setItem(`wa-draft-${c.conversation_id}`, texto); } catch { /* rascunho é conveniência */ }
        setConversa(c);
      })
      .catch(e => { toast.error(e instanceof Error ? e.message : 'Não foi possível abrir a conversa.'); fechar.current(); });
    return () => { vivo = false; };
  }, [alvo]);
  if (!alvo || !conversa) return null;
  return <MiniConversaSheet open telefone={conversa.telefone} conversationId={conversa.conversation_id} onClose={onClose}
    contexto={{ titulo: `${alvo.gerente.nome} · Claro`, detalhe: `Pedido ${alvo.cotacao.numeroPedido} · ${alvo.cotacao.cliente}${alvo.cotacao.cotacao ? ` · cotação ${alvo.cotacao.cotacao}` : ''}` }} />;
}
